import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Plus, Trash2, X, ArrowDownCircle, ArrowUpCircle, Wallet, 
  CreditCard, CheckCircle, FileSpreadsheet 
} from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const DebtsPage = () => {
  const { token } = useAuth();
  const [debts, setDebts] = useState([]);
  const [totals, setTotals] = useState({ owed_to_me: 0, i_owe: 0, net: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [debtForm, setDebtForm] = useState({
    person_name: "",
    debt_type: "owed_to_me",
    amount: "",
    description: ""
  });
  const [transactionForm, setTransactionForm] = useState({
    amount: "",
    transaction_type: "payment",
    description: ""
  });
  const [filter, setFilter] = useState("all");
  const [showDebtDetail, setShowDebtDetail] = useState(false);
  const [selectedDebtDetail, setSelectedDebtDetail] = useState(null);

  const loadDebts = async () => {
    try {
      const res = await api.get(`/debts${filter !== "all" ? `?status=${filter}` : ""}`, token);
      setDebts(res.data.debts);
      setTotals(res.data.totals);
    } catch (error) {
      toast.error("خطأ في تحميل الديون");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDebts();
  }, [token, filter]);

  const addDebt = async () => {
    if (!debtForm.person_name || !debtForm.amount) {
      toast.error("يرجى إدخال الاسم والمبلغ");
      return;
    }
    try {
      await api.post("/debts", {
        ...debtForm,
        amount: parseFloat(debtForm.amount)
      }, token);
      toast.success("تم إضافة الدين بنجاح");
      setShowAddModal(false);
      setDebtForm({ person_name: "", debt_type: "owed_to_me", amount: "", description: "" });
      loadDebts();
    } catch (error) {
      toast.error("خطأ في إضافة الدين");
    }
  };

  const addTransaction = async () => {
    if (!transactionForm.amount) {
      toast.error("يرجى إدخال المبلغ");
      return;
    }
    try {
      await api.post(`/debts/${selectedDebt.id}/transaction`, {
        debt_id: selectedDebt.id,
        ...transactionForm,
        amount: parseFloat(transactionForm.amount)
      }, token);
      
      if (transactionForm.transaction_type === "payment" && (transactionForm.deduct_from_cash || transactionForm.payment_source === "clinic")) {
        await api.post("/transactions", {
          transaction_type: "expense",
          category: "سداد دين",
          amount: parseFloat(transactionForm.amount),
          description: `سداد دين - ${selectedDebt.person_name}`,
          payment_method: "نقدي",
          date: new Date().toISOString().split('T')[0]
        }, token);
        toast.success("تم تسجيل السداد وخصم المبلغ من الصندوق");
      } else {
        toast.success(transactionForm.transaction_type === "payment" ? "تم تسجيل السداد" : "تم إضافة الدين");
      }
      
      setShowTransactionModal(false);
      setTransactionForm({ amount: "", transaction_type: "payment", description: "", deduct_from_cash: false, payment_source: "external" });
      setSelectedDebt(null);
      loadDebts();
    } catch (error) {
      toast.error("خطأ في تسجيل العملية");
    }
  };

  const deleteDebt = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الدين؟")) return;
    try {
      await api.delete(`/debts/${id}`, token);
      toast.success("تم حذف الدين");
      loadDebts();
    } catch (error) {
      toast.error("خطأ في حذف الدين");
    }
  };

  const openTransactionModal = (debt, type) => {
    setSelectedDebt(debt);
    setTransactionForm({ amount: "", transaction_type: type, description: "", payment_source: "external", deduct_from_cash: false });
    setShowTransactionModal(true);
  };

  const viewDebtDetail = (debt) => {
    setSelectedDebtDetail(debt);
    setShowDebtDetail(true);
  };

  const exportDebtsToExcel = () => {
    const wb = XLSX.utils.book_new();
    
    const summaryData = [
      { "البيان": "لي عند الآخرين", "القيمة": totals.owed_to_me },
      { "البيان": "عليّ للآخرين", "القيمة": totals.i_owe },
      { "البيان": "الصافي", "القيمة": totals.net },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, "الملخص");
    
    const debtsData = debts.map(d => ({
      "الاسم": d.person_name,
      "النوع": d.debt_type === "owed_to_me" ? "يطلبني" : "أطلبه",
      "المبلغ الأصلي": d.original_amount || 0,
      "المسدد": (d.original_amount || 0) - (d.current_amount || 0),
      "المتبقي": d.current_amount || 0,
      "الحالة": d.status === "active" ? "نشط" : "مسدد",
      "التاريخ": d.date || "",
      "الملاحظات": d.description || ""
    }));
    const debtsSheet = XLSX.utils.json_to_sheet(debtsData);
    XLSX.utils.book_append_sheet(wb, debtsSheet, "قائمة الديون");
    
    debts.forEach((debt, index) => {
      if (debt.transactions && debt.transactions.length > 0) {
        const personData = [
          { "البيان": "الاسم", "القيمة": debt.person_name },
          { "البيان": "النوع", "القيمة": debt.debt_type === "owed_to_me" ? "يطلبني (لي عنده)" : "أطلبه (عليّ له)" },
          { "البيان": "المبلغ الأصلي", "القيمة": debt.original_amount || 0 },
          { "البيان": "المسدد", "القيمة": (debt.original_amount || 0) - (debt.current_amount || 0) },
          { "البيان": "المتبقي", "القيمة": debt.current_amount || 0 },
          { "البيان": "الحالة", "القيمة": debt.status === "active" ? "نشط" : "مسدد" },
          { "البيان": "", "القيمة": "" },
          { "البيان": "=== سجل العمليات ===", "القيمة": "" },
        ];
        
        const transData = debt.transactions.map(t => ({
          "التاريخ": t.date || "",
          "العملية": t.type === "initial" ? "إنشاء الدين" : t.type === "payment" ? "سداد" : "إضافة دين",
          "المبلغ": t.amount || 0,
          "الرصيد بعدها": t.balance_after !== undefined ? t.balance_after : "-",
          "الملاحظات": t.description || ""
        }));
        
        const ws = XLSX.utils.json_to_sheet(personData);
        XLSX.utils.sheet_add_json(ws, transData, { origin: "A10" });
        
        const sheetName = debt.person_name.substring(0, 28) + (index + 1);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    });
    
    XLSX.writeFile(wb, `تقرير_الديون_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("تم تصدير تقرير الديون مع تفاصيل كل شخص");
  };

  const exportSingleDebt = (debt) => {
    const ws1Data = [
      ["اسم الشخص", debt.person_name],
      ["النوع", debt.debt_type === "owed_to_me" ? "لي عنده (يطلبني)" : "عليّ له (أطلبه)"],
      ["المبلغ الأصلي", debt.original_amount],
      ["المتبقي", debt.current_amount],
      ["المسدد", (debt.original_amount || 0) - (debt.current_amount || 0)],
      ["الحالة", debt.status === "active" ? "نشط" : "مسدد"],
      ["تاريخ الإنشاء", debt.date],
      ["الملاحظات", debt.description || "-"],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(ws1Data);
    
    const transData = [["التاريخ", "العملية", "المبلغ", "الرصيد بعدها", "الملاحظات"]];
    (debt.transactions || []).forEach(t => {
      transData.push([
        t.date,
        t.type === "initial" ? "إنشاء" : t.type === "payment" ? "سداد" : "إضافة",
        t.amount,
        t.balance_after || "-",
        t.description || "-"
      ]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(transData);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, "معلومات الدين");
    XLSX.utils.book_append_sheet(wb, ws2, "سجل العمليات");
    
    XLSX.writeFile(wb, `دين_${debt.person_name}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`تم تصدير سجل ${debt.person_name}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="debts-page">
      <div className="page-header flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-title">الديون</h1>
          <p className="page-subtitle">إدارة الديون والمستحقات</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportDebtsToExcel} className="btn btn-secondary bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
            <FileSpreadsheet className="w-4 h-4" />
            تصدير Excel
          </button>
          <button onClick={() => { setShowAddModal(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="btn btn-primary" data-testid="add-debt-btn">
            <Plus className="w-4 h-4" />
            إضافة دين
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-600 text-sm font-medium">لي عند الآخرين</p>
              <p className="text-2xl font-bold text-emerald-700">{totals.owed_to_me.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
              <ArrowDownCircle className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="card p-5 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-600 text-sm font-medium">عليّ للآخرين</p>
              <p className="text-2xl font-bold text-red-700">{totals.i_owe.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-200 flex items-center justify-center">
              <ArrowUpCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className={`card p-5 bg-gradient-to-br ${totals.net >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>الصافي</p>
              <p className={`text-2xl font-bold ${totals.net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{totals.net.toLocaleString()}</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${totals.net >= 0 ? 'bg-blue-200' : 'bg-orange-200'}`}>
              <Wallet className={`w-6 h-6 ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="tabs mb-6">
        <button className={`tab ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>الكل</button>
        <button className={`tab ${filter === "active" ? "active" : ""}`} onClick={() => setFilter("active")}>نشط</button>
        <button className={`tab ${filter === "paid" ? "active" : ""}`} onClick={() => setFilter("paid")}>مسدد</button>
      </div>

      {/* Debts Table */}
      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-500" />
            سجل الديون ({debts.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-right py-3 px-4 font-medium text-gray-500">الاسم</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">النوع</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">المبلغ الأصلي</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">المتبقي</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">الحالة</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">التاريخ</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {debts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">لا توجد ديون مسجلة</td>
                </tr>
              ) : (
                debts.map((debt) => (
                  <tr key={debt.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button 
                        onClick={() => viewDebtDetail(debt)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {debt.person_name}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        debt.debt_type === "owed_to_me" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}>
                        {debt.debt_type === "owed_to_me" ? "يطلبني" : "أطلبه"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{debt.original_amount?.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`font-bold ${debt.current_amount > 0 ? 
                        (debt.debt_type === "owed_to_me" ? "text-emerald-600" : "text-red-600") : "text-gray-400"}`}>
                        {debt.current_amount?.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        debt.status === "active" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                      }`}>
                        {debt.status === "active" ? "نشط" : "مسدد"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-sm">{debt.date}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2 flex-wrap">
                        {debt.status === "active" && (
                          <>
                            <button 
                              onClick={() => openTransactionModal(debt, "payment")}
                              className="btn btn-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              title="سداد"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => openTransactionModal(debt, "add")}
                              className="btn btn-sm bg-blue-100 text-blue-700 hover:bg-blue-200"
                              title="إضافة دين"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button 
                          onClick={() => deleteDebt(debt.id)}
                          className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Debt Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">إضافة دين جديد</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">اسم الشخص</label>
                <input
                  type="text"
                  className="form-input"
                  value={debtForm.person_name}
                  onChange={(e) => setDebtForm({ ...debtForm, person_name: e.target.value })}
                  placeholder="مثال: قاسم"
                  data-testid="debt-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">نوع الدين</label>
                <select
                  className="form-input"
                  value={debtForm.debt_type}
                  onChange={(e) => setDebtForm({ ...debtForm, debt_type: e.target.value })}
                  data-testid="debt-type-select"
                >
                  <option value="owed_to_me">يطلبني (لي عنده)</option>
                  <option value="i_owe">أطلبه (عليّ له)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">المبلغ</label>
                <input
                  type="number"
                  className="form-input"
                  value={debtForm.amount}
                  onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })}
                  placeholder="150000"
                  data-testid="debt-amount-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ملاحظات (اختياري)</label>
                <input
                  type="text"
                  className="form-input"
                  value={debtForm.description}
                  onChange={(e) => setDebtForm({ ...debtForm, description: e.target.value })}
                  placeholder="سبب الدين..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAddModal(false)} className="btn btn-secondary">إلغاء</button>
              <button onClick={addDebt} className="btn btn-primary" data-testid="save-debt-btn">
                <Plus className="w-4 h-4" />
                إضافة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && selectedDebt && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {transactionForm.transaction_type === "payment" ? "تسجيل سداد" : "إضافة دين"}
              </h2>
              <button onClick={() => setShowTransactionModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <p className="text-sm text-gray-500">الدين لـ</p>
                <p className="font-bold text-lg">{selectedDebt.person_name}</p>
                <p className="text-sm mt-2">
                  المتبقي: <span className="font-bold text-blue-600">{selectedDebt.current_amount?.toLocaleString()}</span>
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">المبلغ</label>
                <input
                  type="number"
                  className="form-input"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                  placeholder="0"
                  data-testid="transaction-amount-input"
                />
                {transactionForm.transaction_type === "payment" && (
                  <div className="flex gap-2 mt-2">
                    <button 
                      onClick={() => setTransactionForm({ ...transactionForm, amount: "50000" })}
                      className="btn btn-sm bg-gray-100 hover:bg-gray-200 flex-1"
                    >50,000</button>
                    <button 
                      onClick={() => setTransactionForm({ ...transactionForm, amount: "100000" })}
                      className="btn btn-sm bg-gray-100 hover:bg-gray-200 flex-1"
                    >100,000</button>
                    <button 
                      onClick={() => setTransactionForm({ ...transactionForm, amount: String(selectedDebt.current_amount) })}
                      className="btn btn-sm bg-blue-100 text-blue-700 hover:bg-blue-200 flex-1"
                    >الكل</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">ملاحظات (اختياري)</label>
                <input
                  type="text"
                  className="form-input"
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  placeholder="..."
                />
              </div>
              {transactionForm.transaction_type === "payment" && (
                <div className="form-group">
                  <label className="form-label">طريقة السداد</label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="payment_source"
                        checked={transactionForm.payment_source === "external" || !transactionForm.payment_source}
                        onChange={() => setTransactionForm({ ...transactionForm, payment_source: "external", deduct_from_cash: false })}
                        className="w-5 h-5 text-blue-600"
                      />
                      <div>
                        <span className="font-medium text-gray-700">سداد خارجي</span>
                        <p className="text-xs text-gray-500">الشخص سدد من عنده (لا يؤثر على حساب المركز)</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-orange-200 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
                      <input
                        type="radio"
                        name="payment_source"
                        checked={transactionForm.payment_source === "clinic"}
                        onChange={() => setTransactionForm({ ...transactionForm, payment_source: "clinic", deduct_from_cash: true })}
                        className="w-5 h-5 text-orange-600"
                      />
                      <div>
                        <span className="font-medium text-orange-700">سداد من المركز</span>
                        <p className="text-xs text-orange-600">سيتم خصم المبلغ من صندوق المركز كمصروف</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowTransactionModal(false)} className="btn btn-secondary">إلغاء</button>
              <button 
                onClick={addTransaction} 
                className={`btn btn-primary ${transactionForm.transaction_type === "payment" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                data-testid="save-transaction-btn"
              >
                <CheckCircle className="w-4 h-4" />
                {transactionForm.transaction_type === "payment" ? "تسجيل السداد" : "إضافة الدين"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debt Detail Modal */}
      {showDebtDetail && selectedDebtDetail && (
        <div className="modal-overlay" onClick={() => setShowDebtDetail(false)}>
          <div className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header sticky top-0 bg-white z-10">
              <h2 className="modal-title flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-500" />
                تفاصيل الدين - {selectedDebtDetail.person_name}
              </h2>
              <button onClick={() => setShowDebtDetail(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="card p-4 text-center">
                  <div className="text-lg font-bold text-gray-700">{selectedDebtDetail.original_amount?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">المبلغ الأصلي</div>
                </div>
                <div className="card p-4 text-center">
                  <div className={`text-lg font-bold ${selectedDebtDetail.debt_type === "owed_to_me" ? "text-emerald-600" : "text-red-600"}`}>
                    {selectedDebtDetail.current_amount?.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">المتبقي</div>
                </div>
                <div className="card p-4 text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {((selectedDebtDetail.original_amount || 0) - (selectedDebtDetail.current_amount || 0)).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">المسدد</div>
                </div>
                <div className="card p-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedDebtDetail.status === "active" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                  }`}>
                    {selectedDebtDetail.status === "active" ? "نشط" : "مسدد"}
                  </span>
                </div>
              </div>

              <div className="card p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">النوع:</span>
                    <span className={`mr-2 font-medium ${selectedDebtDetail.debt_type === "owed_to_me" ? "text-emerald-600" : "text-red-600"}`}>
                      {selectedDebtDetail.debt_type === "owed_to_me" ? "يطلبني (لي عنده)" : "أطلبه (عليّ له)"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">التاريخ:</span>
                    <span className="mr-2 font-medium">{selectedDebtDetail.date}</span>
                  </div>
                  {selectedDebtDetail.description && (
                    <div className="col-span-2">
                      <span className="text-gray-500">الملاحظات:</span>
                      <span className="mr-2">{selectedDebtDetail.description}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="data-table">
                <div className="table-header">
                  <h4 className="table-title">سجل العمليات</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-right py-2 px-3">التاريخ</th>
                        <th className="text-right py-2 px-3">العملية</th>
                        <th className="text-right py-2 px-3">المبلغ</th>
                        <th className="text-right py-2 px-3">الرصيد بعدها</th>
                        <th className="text-right py-2 px-3">الملاحظات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDebtDetail.transactions?.length > 0 ? (
                        selectedDebtDetail.transactions.map((trans, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="py-2 px-3">{trans.date}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                trans.type === "initial" ? "bg-blue-100 text-blue-700" :
                                trans.type === "payment" ? "bg-emerald-100 text-emerald-700" :
                                "bg-amber-100 text-amber-700"
                              }`}>
                                {trans.type === "initial" ? "إنشاء" : trans.type === "payment" ? "سداد" : "إضافة"}
                              </span>
                            </td>
                            <td className={`py-2 px-3 font-medium ${trans.type === "payment" ? "text-emerald-600" : "text-gray-700"}`}>
                              {trans.type === "payment" ? "-" : "+"}{trans.amount?.toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-gray-600">{trans.balance_after?.toLocaleString() || "-"}</td>
                            <td className="py-2 px-3 text-gray-500">{trans.description || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-gray-500">لا توجد عمليات</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => exportSingleDebt(selectedDebtDetail)} className="btn btn-secondary">
                <FileSpreadsheet className="w-4 h-4" />
                تصدير Excel
              </button>
              <button onClick={() => setShowDebtDetail(false)} className="btn btn-secondary">إغلاق</button>
              {selectedDebtDetail.status === "active" && (
                <>
                  <button 
                    onClick={() => {
                      setShowDebtDetail(false);
                      openTransactionModal(selectedDebtDetail, "payment");
                    }}
                    className="btn btn-primary bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle className="w-4 h-4" />
                    تسجيل سداد
                  </button>
                  <button 
                    onClick={() => {
                      setShowDebtDetail(false);
                      openTransactionModal(selectedDebtDetail, "add");
                    }}
                    className="btn btn-primary"
                  >
                    <Plus className="w-4 h-4" />
                    إضافة دين
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtsPage;
